import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, relTime } from '../lib/api'

/**
 * Message streams — transactional vs broadcast. Keeping the two apart protects the
 * reputation that delivers password resets from a marketing blast that draws complaints.
 * Two defaults are seeded per account; more can be created. Defaults can be renamed but not
 * deleted, so a send always has somewhere to land.
 */
export function Streams() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['streams'], queryFn: api.streams.list })
  const [name, setName] = useState('')
  const [type, setType] = useState('transactional')
  const [error, setError] = useState<ApiError | null>(null)

  const create = useMutation({
    mutationFn: () => api.streams.create({ name: name.trim(), type }),
    onSuccess: () => { setName(''); setError(null); qc.invalidateQueries({ queryKey: ['streams'] }) },
    onError: (e) => setError(e as ApiError),
  })
  const toggle = useMutation({
    mutationFn: (s: { id: string; active: boolean }) => s.active ? api.streams.disable(s.id) : api.streams.enable(s.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['streams'] }),
  })
  const remove = useMutation({
    mutationFn: (id: string) => api.streams.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['streams'] }),
  })

  const rows = data?.data ?? []

  return (
    <div>
      <h1>Message streams</h1>
      <p className="lede">
        Separate transactional (receipts, resets) from broadcast (newsletters). Reference a
        stream at send time with <span className="mono">"stream": "slug"</span>; broadcast
        streams are sent as bulk automatically.
      </p>

      <div className="card" style={{ marginBottom: 24 }}>
        <form className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}
              onSubmit={e => { e.preventDefault(); if (name.trim()) create.mutate() }}>
          <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
            <label>New stream name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Product updates" />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Type</label>
            <select value={type} onChange={e => setType(e.target.value)}>
              <option value="transactional">Transactional</option>
              <option value="broadcast">Broadcast</option>
            </select>
          </div>
          <button type="submit" disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </form>
        {error && <div className="alert error" style={{ marginTop: 12, marginBottom: 0 }}>{error.message}</div>}
      </div>

      {isLoading && <div className="skeleton" style={{ height: 160 }} />}

      {rows.length > 0 && (
        <div className="card flush">
          <table className="data">
            <thead><tr><th>Name</th><th>Slug</th><th>Type</th><th>Updated</th><th /></tr></thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.id} style={{ opacity: s.active ? 1 : 0.55 }}>
                  <td className="strong">
                    {s.name}
                    {s.is_default && <span className="badge neutral" style={{ marginLeft: 6 }}>default</span>}
                    {!s.active && <span className="badge failed" style={{ marginLeft: 6 }}>disabled</span>}
                    {s.description && <div className="small muted">{s.description}</div>}
                  </td>
                  <td className="mono small">{s.slug}</td>
                  <td>
                    <span className={`badge ${s.type === 'broadcast' ? 'info' : 'ok'}`}>{s.type}</span>
                  </td>
                  <td className="small muted">{relTime(s.updated_at)}</td>
                  <td className="right">
                    {!s.is_default && (
                      <>
                        <button className="ghost small" onClick={() => toggle.mutate({ id: s.id, active: s.active })}>
                          {s.active ? 'Disable' : 'Enable'}
                        </button>
                        <button className="ghost small danger" onClick={() => {
                          if (confirm(`Delete stream "${s.name}"?`)) remove.mutate(s.id)
                        }}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
